---
title: "The candidate scheduling link"
description: "The public scheduling link in Chosen lets candidates submit their availability. Once everyone required submits, the AI scheduler finds the overlap and books."
category: "scheduling"
keywords:
  - "candidate scheduling link"
  - "self-scheduling link"
  - "submit interview availability"
  - "public scheduling link ATS"
schema: "HowTo"
nav:
  section: "guides"
  order: 70
  parent: "/guides/scheduling"
---

The scheduling link is how a candidate tells Chosen when they're free. It's a private, tokenized link the scheduling email sends them; they open it, submit their availability, and the AI scheduler takes it from there. Interviewers can get one too.

## What the candidate sees

The candidate clicks the link from their scheduling email and lands on a page that asks for their availability. No login, no Chosen account — the token in the link is what identifies them. They pick the times that work, set their timezone, and submit. That's the whole interaction on their end.

## What you see

On your side, each participant's link moves through visible states:

| State | Meaning |
| --- | --- |
| Pending | The link was sent; nobody has opened it yet. |
| Opened | The participant opened the link but hasn't submitted. |
| Submitted | They've submitted their availability. |

Watching the states tells you where a scheduling request is stuck — a link sitting on **pending** for days means the email may not have landed, or the candidate hasn't gotten to it. The [AI events tab](/guides/scheduling/ai-events-tab) flags candidates who go quiet so you don't have to watch every link by hand.

## How a submitted link triggers scheduling

A submitted link feeds the candidate's free times into the overlap finder. The scheduler then needs everyone: it waits until **every required participant** has submitted before it does anything.

Once the last required link comes in, auto-scheduling kicks in on its own. The scheduler matches the candidate's submitted availability against the interviewers' free time, finds the first real overlap, and books — the same flow described in [how the AI scheduler works](/guides/scheduling/ai-scheduler-overview).

<Callout type="info" title="It waits for the full set">
One required participant who hasn't submitted holds up the whole request — there's nothing to solve until every required calendar is in. Optional interviewers don't block it. If a link is stalling things, that's the one to chase.
</Callout>

## When it gets it wrong

- **The candidate can't open the link.** Links are tokenized and time-limited. An old or forwarded link may not work — re-send from the candidate's detail page to issue a fresh one.
- **They submitted but nothing scheduled.** A required participant probably hasn't submitted yet. Check the link states; the request is waiting on whoever is still **pending**.
- **The submitted times don't overlap with anyone.** The scheduler found no common slot. It flags this in the [AI events tab](/guides/scheduling/ai-events-tab) rather than guessing — ask the candidate for more availability, or adjust the interviewer panel.

## Limits

- A link collects availability for one scheduling request. A new interview round means a new link.
- The candidate submits availability; they don't pick the final time. The scheduler chooses the slot from the overlap and confirms it.
- Links expire. A candidate who waits too long needs a re-sent link, not the original.
